import { Alert, Center, Container, Loader, Stack, Text } from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { IconAlertCircle } from '@tabler/icons-react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Navigate, useLocation, useNavigate, useParams } from 'react-router-dom';
import { useMedplum, useMedplumProfile } from '@medplum/react-hooks';
import { useMedplumDirectoryUsers } from '../../hooks/useMedplumDirectoryUsers';
import { practitionerToUser, practicesToUserMap } from '../../lib/adminDirectoryMapping';
import { getUserProfileRouteAccess } from '../../lib/userProfileRouteAccess';
import { usePermissions } from '../../contexts/PermissionContext';
import { UserSettings } from './components/UserSettings';
import { editMember, editPatientProfile } from '../../lib/adminActionsService';
import { patientToUser } from '../../lib/adminDirectoryMapping';
import type { Client } from '../caseload/types';
import { isRoleMembershipOnlyUpdate, type User } from '../users/types/rbac';

/** Build a minimal Client from a User so the client Personal Information section can render when opened from Users (no caseload record). */
function clientFromUser(user: User): Client {
  const [firstName = '', lastName = ''] = user.name.split(/\s+/).filter(Boolean);
  return {
    id: user.id,
    name: user.name,
    firstName: firstName || user.name,
    lastName: lastName || '',
    preferredFirstName: firstName || '',
    preferredLastName: lastName || '',
    pronouns: '',
    clientType: 'Adult',
    status: user.status === 'Active' ? 'Active' : user.status === 'Inactive' ? 'Inactive' : 'Unresponsive',
    relationshipType: 'Primary',
    firstSession: user.createdAt || '',
    mostRecentAppointment: user.lastActive || '',
    nextAppointment: '',
    outstandingItems: [],
    provider: '',
    phone: user.phone,
    email: user.email,
    practice: user.practiceName,
  };
}

/**
 * Standalone route wrapper to view/edit a user's settings by ID (Medplum admin directory).
 */
const USERS_TABS = ['practices', 'practitioners', 'clients'] as const;

function normalizeUsersBackTab(tab: string | undefined): string | undefined {
  if (tab == null) return undefined;
  if (tab === 'providers' || tab === 'staff') return 'practitioners';
  if (tab === 'permissions') return undefined;
  return tab;
}

export function UserSettingsPage(): JSX.Element {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const medplum = useMedplum();
  const sessionProfile = useMedplumProfile();
  const fromTab = (location.state as { fromTab?: string; fromPracticeSettingsTab?: string } | undefined)?.fromTab
    ?? (location.state as { fromPracticeSettingsTab?: string } | undefined)?.fromPracticeSettingsTab;
  const locationCreatedUser = (location.state as { user?: User } | undefined)?.user;
  const { users, setUsers, practices, loading, error, reload } = useMedplumDirectoryUsers();
  const { role: sessionRole } = usePermissions();

  const routeAccess = useMemo(
    () => getUserProfileRouteAccess(id, sessionProfile?.id, sessionRole),
    [id, sessionProfile?.id, sessionRole],
  );

  const practiceMapForMerge = useMemo(() => practicesToUserMap(practices), [practices]);
  const [resolvingProfile, setResolvingProfile] = useState(false);

  useEffect(() => {
    if (!id || !locationCreatedUser || locationCreatedUser.id !== id) return;
    setUsers((prev) => (prev.some((u) => u.id === id) ? prev : [...prev, locationCreatedUser]));
  }, [id, locationCreatedUser, setUsers]);

  // Directory refetch can overwrite navigation state; load the profile directly when missing.
  useEffect(() => {
    if (!id || loading || users.some((u) => u.id === id)) {
      setResolvingProfile(false);
      return;
    }
    let cancelled = false;
    setResolvingProfile(true);
    void (async () => {
      try {
        const patient = await medplum.readResource('Patient', id);
        if (cancelled) return;
        const fromPatient = patientToUser(patient);
        if (fromPatient) {
          setUsers((prev) => (prev.some((u) => u.id === id) ? prev : [...prev, fromPatient]));
          return;
        }
      } catch {
        /* not a Patient or no access */
      }
      try {
        const practitioner = await medplum.readResource('Practitioner', id);
        if (cancelled) return;
        const fromPractitioner = practitionerToUser(practitioner, practiceMapForMerge);
        if (fromPractitioner) {
          setUsers((prev) => (prev.some((u) => u.id === id) ? prev : [...prev, fromPractitioner]));
        }
      } catch {
        /* not found */
      }
    })().finally(() => {
      if (!cancelled) setResolvingProfile(false);
    });
    return () => {
      cancelled = true;
      setResolvingProfile(false);
    };
  }, [id, loading, medplum, practiceMapForMerge, setUsers, users]);

  const user = useMemo(() => (id ? users.find((u) => u.id === id) ?? null : null), [users, id]);
  const client = useMemo<Client | undefined>(
    () => (user?.role === 'client' ? clientFromUser(user) : undefined),
    [user],
  );

  const handleBack = useCallback(() => {
    const tab = normalizeUsersBackTab(fromTab);
    if (tab && (USERS_TABS as readonly string[]).includes(tab)) {
      navigate('/dashboard/settings/practices-users', { state: { tab } });
    } else {
      navigate(-1);
    }
  }, [fromTab, navigate]);

  const handleUpdateUser = useCallback(
    async (userId: string, updates: Partial<User>) => {
      setUsers((prev) => prev.map((u) => (u.id === userId ? { ...u, ...updates } : u)));

      if (isRoleMembershipOnlyUpdate(updates)) {
        return;
      }

      const targetUser = users.find((u) => u.id === userId);
      const isClient = targetUser?.role === 'client';

      try {
        if (isClient) {
          const patient = await editPatientProfile(medplum, userId, updates);
          const fromServer = patientToUser(patient);
          if (fromServer) {
            setUsers((prev) => prev.map((u) => (u.id !== userId ? u : { ...u, ...fromServer })));
          } else {
            reload();
          }
        } else {
          const practitioner = await editMember(medplum, userId, updates);
          const fromServer = practitionerToUser(practitioner, practiceMapForMerge);
          if (fromServer) {
            setUsers((prev) =>
              prev.map((u) => {
                if (u.id !== userId) return u;
                return {
                  ...u,
                  ...fromServer,
                  permissions: u.permissions,
                  credentialing: u.credentialing,
                  locationIds: u.locationIds,
                };
              }),
            );
          } else {
            reload();
          }
        }
        notifications.show({
          title: 'Saved',
          message: 'Profile changes were saved.',
          color: 'green',
        });
      } catch (err) {
        console.error('[admin-actions] edit-members failed:', err);
        reload();
        notifications.show({
          title: 'Save failed',
          message: err instanceof Error ? err.message : 'Could not save profile. Showing data from the server.',
          color: 'red',
        });
      }
    },
    [medplum, practiceMapForMerge, reload, setUsers, users],
  );

  const handleDeleteUser = useCallback(
    (userId: string) => {
      setUsers((prev) => prev.filter((u) => u.id !== userId));
      navigate(-1);
    },
    [setUsers, navigate],
  );

  if (!id) {
    return (
      <Container size="lg" py="xl">
        <Alert icon={<IconAlertCircle size={20} />} color="red" title="Missing user id">
          No user id provided.
        </Alert>
      </Container>
    );
  }

  if (routeAccess === 'pending') {
    return (
      <Container size="lg" py="xl">
        <Center py="xl">
          <Loader size="lg" />
        </Center>
      </Container>
    );
  }

  if (user?.role === 'client') {
    return <Navigate to={`/dashboard/clients/${user.id}/profile`} replace />;
  }

  if (sessionProfile?.id && id === sessionProfile.id) {
    return <Navigate to="/dashboard/settings/profile" replace />;
  }

  if (routeAccess === 'denied') {
    return <Navigate to="/dashboard/settings/profile" replace />;
  }

  if (loading) {
    return (
      <Container size="lg" py="xl">
        <Center py="xl">
          <Loader size="lg" />
        </Center>
      </Container>
    );
  }

  if (error) {
    return (
      <Container size="lg" py="xl">
        <Alert color="red" title="Could not load user directory">
          {error.message}
        </Alert>
      </Container>
    );
  }

  if (!user && resolvingProfile) {
    return (
      <Container size="lg" py="xl">
        <Center py="xl">
          <Loader size="lg" />
        </Center>
      </Container>
    );
  }

  if (!user) {
    return (
      <Container size="lg" py="xl">
        <Alert icon={<IconAlertCircle size={20} />} color="red" title="User not found">
          <Stack gap={4}>
            <Text>User with id &quot;{id}&quot; was not found.</Text>
            <Text c="dimmed" size="sm">
              Try returning to Practices & Users or the originating page and selecting a different user.
            </Text>
          </Stack>
        </Alert>
      </Container>
    );
  }

  return (
    <UserSettings
      user={user}
      onBack={handleBack}
      onUpdateUser={handleUpdateUser}
      onDeleteUser={handleDeleteUser}
      isPlatformView={true}
      allUsers={users}
      practices={practices}
      client={client ?? undefined}
    />
  );
}
